/**
 * Server-side pagination — shared types + hook.
 *
 * All list endpoints return a `Paginated<T>` envelope. Pages use
 * `usePaginationState()` for the page + page_size state, then render
 * the shared `<Pagination>` component below the table.
 *
 * Page sizes are fixed to a closed set so the user picks from a chip
 * group instead of typing a number. Backend validates the same set.
 */
import * as React from "react";

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 25;

/** Envelope returned by every paginated list endpoint. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Hook that owns `page` + `pageSize` state and exposes setters that
 * reset the page to 1 when the page size changes (or when callers
 * invoke `reset()` after a filter change).
 */
export function usePaginationState(initialPageSize: PageSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSizeRaw] = React.useState<PageSize>(initialPageSize);

  const setPageSize = React.useCallback((next: PageSize) => {
    setPageSizeRaw(next);
    setPage(1);
  }, []);

  const reset = React.useCallback(() => setPage(1), []);

  return { page, pageSize, setPage, setPageSize, reset };
}

/** Build the `?page=&page_size=` query slice for an api-client call. */
export function pageQuery(page: number, page_size: PageSize): { page: number; page_size: number } {
  return { page, page_size };
}
